"use client";

import { useLinkStatus } from "next/link";
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type RefObject } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { NAVIGATION_PROGRESS_DELAY_MS, claimNavigationProgress, handOffNavigationProgress, releaseNavigationProgress, clearNavigationProgressHandoff, navigationProgressSnapshot, navigationProgressServerSnapshot, subscribeNavigationProgress } from "@/lib/navigation-progress-handoff";
import { createPortal } from "react-dom";
import { useSiteUiCopy } from "@/components/layout/use-site-ui-copy";

function ProgressRail() {
  const copy = useSiteUiCopy();
  return createPortal(
    <span className="cc-page-loading__rail" role="status" aria-label={copy.loadingPage} />,
    document.body,
  );
}

export function CcPendingProgress({ pendingRef, href }: { pendingRef?: RefObject<boolean>; href?: string | null } = {}) {
  const [visible, setVisible] = useState(false);
  const owner = useRef(Symbol());
  const startedAt = useRef(0);
  useLayoutEffect(() => {
    startedAt.current = Date.now();
    const sourceUrl = window.location.href;
    const token = owner.current;
    const source = new URL(sourceUrl);
    const destination = href ? new URL(href, sourceUrl) : null;
    const canHandOff = href !== null && (!destination || `${destination.pathname}${destination.search}` !== `${source.pathname}${source.search}`);
    if (pendingRef && canHandOff) claimNavigationProgress(token);
    return () => {
      if (pendingRef?.current && canHandOff) handOffNavigationProgress({ owner: token, sourceUrl, startedAt: startedAt.current });
      else if (pendingRef) releaseNavigationProgress(token);
    };
  }, [pendingRef, href]);
  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), Math.max(0, NAVIGATION_PROGRESS_DELAY_MS - (Date.now() - startedAt.current)));
    return () => window.clearTimeout(timer);
  }, []);
  return visible ? <ProgressRail /> : null;
}

/** Shared-layout fallback only for a still-pending link that unmounts. It keeps
 * the original deadline instead of restarting the bar after a menu animation. */
export function CcNavigationProgressHandoff() {
  const pathname = usePathname();
  const search = useSearchParams()?.toString();
  const handoff = useSyncExternalStore(subscribeNavigationProgress, navigationProgressSnapshot, navigationProgressServerSnapshot);
  const [visible, setVisible] = useState(false);
  useLayoutEffect(() => {
    if (handoff && window.location.href !== handoff.sourceUrl) clearNavigationProgressHandoff();
  }, [handoff, pathname, search]);
  useLayoutEffect(() => {
    setVisible(false);
    if (!handoff) return;
    const remaining = NAVIGATION_PROGRESS_DELAY_MS - (Date.now() - handoff.startedAt);
    if (remaining <= 0) { setVisible(true); return; }
    const timer = window.setTimeout(() => setVisible(true), remaining);
    return () => window.clearTimeout(timer);
  }, [handoff]);
  useEffect(() => {
    window.addEventListener("popstate", clearNavigationProgressHandoff);
    return () => { window.removeEventListener("popstate", clearNavigationProgressHandoff); clearNavigationProgressHandoff(); };
  }, []);
  return handoff && visible ? <ProgressRail /> : null;
}

/** Fast transitions never mount a rail; an exiting menu can hand a slow one to
 * the persistent layout until the URL commits or another navigation replaces it. */
export function CcNavigationProgress({ href }: { href?: string | null }) {
  const { pending } = useLinkStatus();
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  return pending ? <CcPendingProgress pendingRef={pendingRef} href={href} /> : null;
}
